import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { initAnalytics } from "@/lib/analytics";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Products from "@/pages/Products";
import ProductCategory from "@/pages/ProductCategory";
import ProductDetail from "@/pages/ProductDetail";
import Industries from "@/pages/Industries";
import IndustryDetail from "@/pages/IndustryDetail";
import Capabilities from "@/pages/Capabilities";
import Quality from "@/pages/Quality";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Resources from "@/pages/Resources";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import RequestQuote from "@/pages/RequestQuote";
import Markets from "@/pages/Markets";
import SmartSolutions from "@/pages/SmartSolutions";
import KnowledgeCenter from "@/pages/KnowledgeCenter";
import KnowledgeArticle from "@/pages/KnowledgeArticle";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SiteLayout() {
  return (
    <>
      <Header />
      <main id="main-content">
        <Outlet />
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}

function App() {
  useEffect(() => {
    initAnalytics();
    const isFine = window.matchMedia("(pointer: fine)").matches;
    if (!isFine) return;
    const lenis = new Lenis({ lerp: 0.16, smoothWheel: true, autoRaf: true });
    window.__lenis = lenis;
    return () => { lenis.destroy(); window.__lenis = null; };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/category/:slug" element={<ProductCategory />} />
            <Route path="/products/:slug" element={<ProductDetail />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/industries/:slug" element={<IndustryDetail />} />
            <Route path="/capabilities" element={<Capabilities />} />
            <Route path="/quality" element={<Quality />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:slug" element={<ProjectDetail />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/insights" element={<Blog />} />
            <Route path="/insights/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/request-a-quote" element={<RequestQuote />} />
            <Route path="/markets" element={<Markets />} />
            <Route path="/smart-solutions" element={<SmartSolutions />} />
            <Route path="/knowledge-center" element={<KnowledgeCenter />} />
            <Route path="/knowledge" element={<Navigate to="/knowledge-center" replace />} />
            <Route path="/knowledge-center/:slug" element={<KnowledgeArticle />} />
            <Route path="/privacy-policy" element={<Legal kind="privacy" />} />
            <Route path="/terms" element={<Legal kind="terms" />} />
            <Route path="/cookie-policy" element={<Legal kind="cookies" />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
